import { useEffect, useState } from "react";
import { Star, Loader2, MessageSquarePlus, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../api";
import Reveal from "./Reveal";
import { toast } from "sonner";

function Stars({ value, size = "w-4 h-4" }) {
    return (
        <span className="flex gap-0.5" aria-label={`${value} out of 5 stars`}>
            {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className={`${size} ${i <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
            ))}
        </span>
    );
}

export default function Reviews() {
    const [reviews, setReviews] = useState([]);
    const [name, setName] = useState("");
    const [rating, setRating] = useState(0);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    }, []);

    const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

    const submit = async (e) => {
        e.preventDefault();
        if (!rating) {
            toast.error("Please choose a star rating");
            return;
        }
        setSending(true);
        try {
            await api.post("/reviews", { name, rating, text });
            setSubmitted(true);
            setName("");
            setRating(0);
            setText("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-700 focus:ring-2 focus:ring-cyan-100 transition-all duration-300";

    return (
        <section id="reviews" data-testid="reviews-section" className="py-16 md:py-24 bg-white border-t border-slate-200">
            <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <Reveal>
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="font-jbm text-xs uppercase tracking-[0.25em] text-cyan-700 mb-3">07 — Reviews</div>
                            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
                                Trusted by research teams
                            </h2>
                        </div>
                        {reviews.length > 0 && (
                            <div className="flex items-center gap-3" data-testid="reviews-summary">
                                <Stars value={Math.round(avg)} size="w-5 h-5" />
                                <span className="font-display text-xl font-bold text-slate-900">{avg.toFixed(1)}</span>
                                <span className="font-jbm text-xs text-slate-500">from {reviews.length} review{reviews.length !== 1 ? "s" : ""}</span>
                            </div>
                        )}
                    </div>
                </Reveal>

                <div className="grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-7 space-y-5" data-testid="reviews-grid">
                        {reviews.length === 0 ? (
                            <Reveal>
                                <div className="border border-dashed border-slate-300 rounded-2xl p-10 text-center" data-testid="reviews-empty">
                                    <MessageSquarePlus className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                                    <p className="font-display text-lg text-slate-700">No reviews published yet</p>
                                    <p className="text-sm text-slate-500 mt-1">Be the first to share your experience.</p>
                                </div>
                            </Reveal>
                        ) : (
                            reviews.map((r, i) => (
                                <Reveal key={r.id} delay={Math.min(i * 0.06, 0.3)}>
                                    <figure className="bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6" data-testid={`review-card-${r.id}`}>
                                        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                                            <div className="flex items-center gap-3">
                                                <span className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center font-display font-bold text-sm">
                                                    {r.name.charAt(0).toUpperCase()}
                                                </span>
                                                <figcaption className="font-display font-semibold text-slate-900">{r.name}</figcaption>
                                            </div>
                                            <Stars value={r.rating} />
                                        </div>
                                        <blockquote className="text-sm leading-relaxed text-slate-600">{r.text}</blockquote>
                                    </figure>
                                </Reveal>
                            ))
                        )}
                    </div>

                    <div className="lg:col-span-5">
                        <Reveal delay={0.1}>
                            {submitted ? (
                                <div className="bg-[#F8FAFC] border border-slate-200 rounded-3xl p-8 text-center" data-testid="review-success">
                                    <div className="w-12 h-12 rounded-full bg-cyan-50 border border-cyan-200 flex items-center justify-center mx-auto mb-4">
                                        <ShieldCheck className="w-5 h-5 text-cyan-700" />
                                    </div>
                                    <h3 className="font-display text-xl font-semibold text-slate-900">Thanks for your review</h3>
                                    <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                                        Every review is read by our team before publishing — yours will appear here once approved.
                                    </p>
                                    <button
                                        data-testid="review-another-button"
                                        onClick={() => setSubmitted(false)}
                                        className="mt-6 text-sm font-medium text-slate-600 hover:text-cyan-800 underline underline-offset-4 transition-colors duration-300"
                                    >
                                        Write another
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="bg-[#F8FAFC] border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-5" data-testid="review-form">
                                    <h3 className="font-display text-xl font-semibold tracking-tight text-slate-900">Leave a review</h3>
                                    <div>
                                        <label className="font-jbm text-[10px] uppercase tracking-widest text-slate-500 block mb-2">Your rating *</label>
                                        <div className="flex gap-1.5" data-testid="review-stars">
                                            {[1, 2, 3, 4, 5].map((i) => (
                                                <button
                                                    key={i}
                                                    type="button"
                                                    data-testid={`review-star-${i}`}
                                                    onClick={() => setRating(i)}
                                                    aria-label={`${i} star${i !== 1 ? "s" : ""}`}
                                                    className="p-1 transition-transform duration-200 hover:scale-125"
                                                >
                                                    <Star className={`w-7 h-7 ${i <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-300"}`} />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="font-jbm text-[10px] uppercase tracking-widest text-slate-500 block mb-2">Name or initials *</label>
                                        <input data-testid="review-name-input" required value={name} onChange={(e) => setName(e.target.value)} maxLength={60} className={inputCls} placeholder="e.g. Dr S. or M.K." />
                                    </div>
                                    <div>
                                        <label className="font-jbm text-[10px] uppercase tracking-widest text-slate-500 block mb-2">Your review *</label>
                                        <textarea
                                            data-testid="review-text-input"
                                            required
                                            rows={4}
                                            value={text}
                                            onChange={(e) => setText(e.target.value)}
                                            maxLength={800}
                                            className={`${inputCls} resize-none`}
                                            placeholder="How was the quality, packaging, delivery and communication?"
                                        />
                                    </div>
                                    <button
                                        data-testid="review-submit-button"
                                        type="submit"
                                        disabled={sending}
                                        className="w-full flex items-center justify-center gap-2.5 bg-slate-900 text-white font-medium py-3.5 rounded-full hover:bg-cyan-800 disabled:opacity-60 transition-colors duration-300"
                                    >
                                        {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                                        {sending ? "Submitting…" : "Submit review"}
                                    </button>
                                    <p className="text-xs text-slate-400 text-center">Reviews are moderated before appearing on this page.</p>
                                </form>
                            )}
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
